<script>
  import t from '#lib/i18n.svelte.js';
  import Image from '#lib/Image.svelte';
  import { lazy } from '#lib/lazy.svelte.js';
  const entry_picker_drawer = lazy(() => import('#lib/EntryPickerDrawer.svelte'));
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import Dropdown from '#lib/Dropdown.svelte';

  let { 
    selected_entry_ids,
    image_ref = $bindable(),  // image_ref holds the entry_id of an image_entry
    image, 
    aspect_ratio, 
    is_editing, 
    placeholder, 
    hint,
    onselect,
    style,
    wrapper_style,
    onremove,
    can_remove_empty = false,
    /** @type {boolean} Allow selecting multiple images (requires onmultiselect callback) */
    multi_select = false,
    /** @type {(entry_ids: string[]) => void} Called with all selected IDs when multi_select is true */
    onmultiselect,
    ...rest
  } = $props();

  let picker_open = $state(false);

  function handle_select(entry_id, type) {
    onselect && onselect(entry_id, type);
    image_ref = entry_id;
  }

  function handle_remove() {
    // Removal has to be handled by the parent component
    onremove && onremove();
  }

  function handle_picker_confirm(selected_ids) {
    if (selected_ids.length === 0) {
      picker_open = false;
      return;
    }

    if (multi_select && onmultiselect) {
      // Multi-select mode: pass all IDs to callback
      onmultiselect(selected_ids);
    } else {
      // Single-select mode: just use the first one
      handle_select(selected_ids[0], 'image');
    }
    picker_open = false;
  }
</script>

<div class='image-ref flex-column gap-y g-4' style={wrapper_style}>
  {#if is_editing}
    {#if image_ref}
      <Image {style} {aspect_ratio} {...image} {is_editing} {placeholder} {hint} />
    {:else}
      <button onclick={() => { picker_open = true; }}>
        <Image {style} {aspect_ratio} {...image} {is_editing} {placeholder} {hint} />
      </button>
    {/if}

    <div class='image-edit-tools'>
      {#if image_ref}
        <Dropdown
          variant='brand encased'
          start_icon='dot-dot-dot'
          placement='bottom-end'
          show_arrow={false}
        >
          {#snippet children(popover_ref)}
            <div class='flex-column'>
              <button onclick={() => { popover_ref.hidePopover(); picker_open = true; }} class='dropdown-item'>
                <ButtonLabel variant='brand ghostly' start_icon='cycle' align='start-stretch'>
                  {t('replace_image')}
                </ButtonLabel>
              </button>
              <button onclick={() => { popover_ref.hidePopover(); handle_remove(); }} class='dropdown-item'>
                <ButtonLabel variant='brand ghostly' start_icon='trash' align='start-stretch'>
                  {t('remove')}
                </ButtonLabel>
              </button>
            </div>
          {/snippet}
        </Dropdown>
      {:else if can_remove_empty}
        <button onclick={handle_remove} class='dropdown-item'>
          <ButtonLabel variant='brand encased' start_icon='close' />
        </button>
      {/if}
    </div>
  {:else if image_ref}
    <Image {aspect_ratio} {...image} {is_editing} {placeholder} {hint} {...rest} />
  {/if}
</div>

{#if picker_open && entry_picker_drawer.component}
  {@const excluded = selected_entry_ids ? selected_entry_ids : image_ref ? [image_ref] : []}
  <entry_picker_drawer.component
    entry_type='image'
    initial_selected={[]}
    excluded_ids={excluded}
    onconfirm={handle_picker_confirm}
    oncancel={() => { picker_open = false; }}
    confirm_label={multi_select ? t('add_selected_entry') : t('select_image')}
    confirm_label_plural={t('add_selected_entries', { count: '{{count}}' })}
    {multi_select}
    initial_view='grid'
  />
{/if}

<style lang='postcss'>
  .image-ref {
    position: relative;
    :global( > .media-item){
      margin-inline: auto;
    }
  }
  .image-edit-tools {
    position: absolute;
    top: var(--size-4);
    right: var(--size-4);
  }
</style>
